import React, { useState, useEffect, useMemo } from "react";
import { AppProvider, useAppContext } from "@/AppContext";
import { ReactFlowProvider } from "@xyflow/react";
import { useLocation, useParams } from "react-router-dom";
import PageTop from "@/Pages/Project/PageTop";
import PageSeletor from "@/Pages/Project/Flow/Right/PageSeleltor";
import { fieldMap, typeMap } from "@/config/dataMap";
import { rebuildFormula } from "@/config/func";
import { jobs,getJob } from "@/services/common";
import userInfo from "@/Components/user";
import { Button, message, Dropdown, Checkbox, Spin } from "antd";
import "./../index.less";
import ReactJson from "react-json-view";
import { CopyOutlined, DownOutlined, CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { designValue, getDesignValue } from "@/services/design";

const Component = () => {
  const { state } = useAppContext();
  const location = useLocation();
  const params = useParams<{ k: string; f: string; j: string }>();

  const [tabList, setTabList] = useState<{ key: any; label: any }[]>([]);
  const [activeTab, setActiveTab] = useState<any>("");
  const [taskInputData, setTaskInputData] = useState<any>(null);
  const [activeElement, setActiveElement] = useState<string | null>(null);
  const [activeStream, setActiveStream] = useState<string | null>(null);
  const [input, setInput] = useState<any>({});
  const [showFullInput, setShowFullInput] = useState(false);
  const [isDeviceOpen, setIsDeviceOpen] = useState(true);
  const [isStreamOpen, setIsStreamOpen] = useState(false);

  // 对比功能
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<"all" | "design" | "actual" | "task">("all");
  const [compareItems, setCompareItems] = useState<any[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [taskDataList, setTaskDataList] = useState<any[]>([]); // 任务数据列表
  const [confirmLoading, setConfirmLoading] = useState(false); // 确认选择时的 loading 状态

  // 获取对比列表
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await designValue({ flow_id: params.f }, userInfo.access_token);
      setDataList(res.data || []);
    } catch (err) {
      console.error("获取失败", err);
    } finally {
      setLoading(false);
    }
  };
  // 获取任务数据
      const fetchTaskData = async () => {
          setLoading(true);
          try {
              const res = await jobs({ flow_id: params.f }, userInfo.access_token);
              const allTaskData = res.data.data || [];
            const filteredData = params.j
                ? allTaskData.filter((item: any) => String(item.id) !== String(params.j))
                : allTaskData;
            setTaskDataList(filteredData);
          } catch (err) {
              console.error("获取任务数据失败", err);
          } finally {
              setLoading(false);
          }
      };

  const handleDropdownOpen = (open: boolean) => {
    setDropdownOpen(open);
    if (open) {
      fetchData();
      fetchTaskData();
    }
  };

  const filteredList = useMemo(() => {
          if (filterType === "all") {
              // 全部：显示设计值 + 实际值 + 任务数据
              return [
                  ...dataList.map(item => ({ ...item, source: 'design' })),
                  ...taskDataList.map(item => ({ ...item, source: 'task' }))
              ];
          } else if (filterType === "design") {
              return dataList.filter(item => item.type === "design").map(item => ({ ...item, source: 'design' }));
          } else if (filterType === "actual") {
              return dataList.filter(item => item.type === "actual").map(item => ({ ...item, source: 'design' }));
          } else if (filterType === "task") {
              return taskDataList.map(item => ({ ...item, source: 'task' }));
          }
          return [];
      }, [dataList, taskDataList, filterType]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

const handleConfirm = async () => {
        setConfirmLoading(true); // 开始 loading
        try {
            const fetchPromises = selectedIds.map(async (id) => {
                try {
                    // 在 filteredList 中查找对应的项目，判断来源
                    const selectedItem = filteredList.find(item => item.id === id);
                    if (selectedItem?.source === 'task') {
                        // 任务数据：使用 getJob 接口
                        const res = await getJob(id, userInfo.access_token);
                        const data ={
                            id:res.data.id,
                            values:{
                            name:res.data.name,  
                            mix_input :JSON.parse(res.data.input),
                            } ,
                            type:"task"
                        }
                        return data;
                    } else {
                        // 设计值/实际值：使用 getDesignValue 接口
                        const res = await getDesignValue(id, userInfo.access_token);
                        return res.data;
                    }
                } catch (err) {
                    console.error("获取失败", id, err);
                    return null;
                }
            });
            // 2. 等待所有请求完成
            let selectedItems = await Promise.all(fetchPromises);
            // 3. 过滤掉失败的
            selectedItems = selectedItems.filter(Boolean);
            setCompareItems(selectedItems);
            setDropdownOpen(false);
        } finally {
            setConfirmLoading(false); // 结束 loading
        }
    };

  // 加载任务数据
  useEffect(() => {
    getJob(params.j, userInfo.access_token).then((r) => {
      if (r.status === 200) {
        const data = {
          name: r.data.name,
          flow_id: r.data.flow_id,
          description: r.data.description,
          inputs: JSON.parse(r.data.input),
        };
        setInput(data);
      }
    });
  }, []);

  useEffect(() => {
    if (input?.inputs) {
      setTaskInputData(input);
      const tabs = generateTabListFromData(input);
      setTabList(tabs);
      if (tabs.length > 0) setActiveTab(tabs[0].key);
    }
  }, [input]);

  // 生成 Tab
  const generateTabListFromData = (data: any) => {
    if (!data?.inputs?.elements) return [];
    const sections = data.inputs.elements.map(el => el.section).filter(Boolean);
    return Array.from(new Set(sections)).map(sec => ({ key: sec, label: sec }));
  };

  // 获取当前 Tab 设备/流股
  const getElementsByTab = (tabKey) => {
    if (!taskInputData?.inputs?.elements) return [];
    const list = taskInputData.inputs.elements;
    return tabKey ? list.filter(el => el.section === tabKey && el.type !== "Mixer") : list.filter(el => el.type !== "Mixer");
  };

  const getStreamsByTab = (tabKey) => {
    if (!taskInputData?.inputs?.streams) return [];
    if (!tabKey) return taskInputData.inputs.streams;
    const elms = taskInputData.inputs.elements.filter(el => el.section === tabKey);
    const names = new Set();
    elms.forEach(el => {
      el.inputStreams?.forEach(s => names.add(s.name));
      el.outputStreams?.forEach(s => names.add(s.name));
    });
    return taskInputData.inputs.streams.filter(s => names.has(s.name));
  };

  const elements = getElementsByTab(tabList.length ? activeTab : null);
  const streams = getStreamsByTab(tabList.length ? activeTab : null);

  useEffect(() => {
    if (taskInputData && !showFullInput) {
      const els = getElementsByTab(tabList.length ? activeTab : null);
      const sts = getStreamsByTab(tabList.length ? activeTab : null);
      if (els.length) { setActiveElement(els[0].name); setActiveStream(null); }
      else if (sts.length) { setActiveStream(sts[0].name); setActiveElement(null); }
    }
  }, [activeTab, taskInputData, showFullInput]);

  // ==============================================
  //  获取当前设备 / 流股 以及 所有对比数据
  // ==============================================
  const currentEl = activeElement
    ? taskInputData?.inputs?.elements?.find(e => e.name === activeElement)
    : null;

  const currentSt = activeStream
    ? taskInputData?.inputs?.streams?.find(s => s.name === activeStream)
    : null;

  const compareElementList = compareItems.map(item =>
    item?.values?.mix_input?.elements?.find(e => e.name === activeElement) || null
  );

  const compareStreamList = compareItems.map(item =>
    item?.values?.mix_input?.streams?.find(s => s.name === activeStream) || null
  );

  // 表格列构建
  const tableColumns = [
    { title: "参数名称", key: "label" },
    { title: "当前值", key: "current" },
    ...compareItems.map((item, i) => ({
      // title: `${item.type === "design" ? "设计值" : "实际值"}(${item.values.name || item.id})`,
      title: `${item.type === "task" ? item.values.name : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}`,
      key: `compare_${i}`,
      type: item.type,
    })),
  ];

  // 生成设备参数表格行
  const getElementTableRows = () => {
    if (!currentEl) return [];
    const rows = [];
    const cfg = fieldMap.elements;
    const data = { ...currentEl, ...currentEl.params };

    // 计算误差百分比
    const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
      if (compareValue === 0 && calcValue === 0) return "0.00%";
      if (compareValue === 0 && calcValue !== 0) return "100.00%";
      const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
      return `${errorPercent.toFixed(2)}%`;
    };

    Object.entries(cfg).forEach(([key, c]) => {
      if (data[key] === undefined || data[key] === null) return;
      const val = c.enum?.[data[key]] ?? data[key];
      const unit = data[c.unitKey] ?? c.unit ?? "";

      rows.push({
        label: c.name,
        current: `${val} ${unit}`,
        compares: compareElementList.map(cel => {
          if (!cel) return "-";
          const d = { ...cel, ...cel.params };
          const v = c.enum?.[d[key]] ?? d[key];
          if (v === undefined || v === null) return "-";
          
          // 判断是否为数值，只有数值才计算误差百分比
          const currentNum = parseFloat(val);
          const compareNum = parseFloat(v);
          const isNumeric = !isNaN(currentNum) && !isNaN(compareNum);
          
          if (isNumeric) {
            const errorPercent = (compareNum !== 0)
              ? calculateErrorPercent(compareNum, currentNum)
              : (currentNum === 0) ? "0%" : "100%";
            return `${v} ${unit}\u00A0\u00A0\u00A0(${errorPercent})`;
          } else {
            // 非数值类型，直接显示
            return `${v} ${unit}`;
          }
        }),
      });
    });

    // 进料位置
    if (currentEl.params.Ns && currentEl.inputStreams) {
      currentEl.inputStreams.forEach((s, i) => {
        rows.push({
          label: `${s.name} 进料位置`,
          current: currentEl.params.Ns[i] ?? "-",
          compares: compareElementList.map(cel => cel?.params?.Ns?.[i] ?? "-"),
        });
      });
    }

    // 出料位置
    if (currentEl.params.No && currentEl.outputStreams) {
      currentEl.outputStreams.forEach((s, i) => {
        rows.push({
          label: `${s.name} 出料位置`,
          current: currentEl.params.No[i] ?? "-",
          compares: compareElementList.map(cel => cel?.params?.No?.[i] ?? "-"),
        });
      });
    }

    // 支持任意多组 塔板效率
    if (currentEl.params.eff && currentEl.params.eff.length > 0) {
      currentEl.params.eff.forEach((eff, idx) => {
        const prefix = idx + 1;
        rows.push(
          { label: `塔板效率 ${prefix} - 起始塔板`, current: eff.start ?? "-", compares: compareElementList.map(c => c?.params?.eff?.[idx]?.start ?? "-") },
          { label: `塔板效率 ${prefix} - 结束塔板`, current: eff.end ?? "-", compares: compareElementList.map(c => c?.params?.eff?.[idx]?.end ?? "-") },
          { label: `塔板效率 ${prefix} - 效率值`, current: eff.value ?? "-", compares: compareElementList.map(c => c?.params?.eff?.[idx]?.value ?? "-") }
        );
      });
    }

    return rows;
  };

  // 生成流股参数表格行
  const getStreamTableRows = () => {
    if (!currentSt) return [];
    const rows = [];
    const cfg = fieldMap.streams;
    const data = currentSt.params;

    // 计算误差百分比
    const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
      if (compareValue === 0 && calcValue === 0) return "0.00%";
      if (compareValue === 0 && calcValue !== 0) return "100.00%";
      const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
      return `${errorPercent.toFixed(2)}%`;
    };

    Object.entries(cfg).forEach(([key, c]) => {
      if (data[key] === undefined || data[key] === null) return;
      const val = c.enum?.[data[key]] ?? data[key];
      const unit = data[c.unitKey] ?? c.unit ?? "";
      rows.push({
        label: c.name,
        current: `${val} ${unit}`,
        compares: compareStreamList.map(cst => {
          if (!cst) return "-";
          const v = c.enum?.[cst.params[key]] ?? cst.params[key];
          if (v === undefined) return "-";
          
          // 判断是否为数值，只有数值才计算误差百分比
          const currentNum = parseFloat(val);
          const compareNum = parseFloat(v);
          const isNumeric = !isNaN(currentNum) && !isNaN(compareNum);
          
          if (isNumeric) {
            const errorPercent = (compareNum !== 0)
              ? calculateErrorPercent(compareNum, currentNum)
              : (currentNum === 0) ? "0.00%" : "100.00%";
            return `${v} ${unit}(${errorPercent})`;
          } else {
            // 非数值类型，直接显示
            return `${v} ${unit}`;
          }
        }),
      });
    });

    // 组分
    if (currentSt.materials?.length) {
      currentSt.materials.forEach((mat, i) => {
        const name = mat.cas === "7732-18-5" ? "水" : mat.cas === "67-56-1" ? "甲醇" : mat.name;
        rows.push({
          label: `${rebuildFormula(name)} 摩尔分率`,
          current: mat.molar_frac,
          compares: compareStreamList.map(cst => {
            const compareVal = cst?.materials?.[i]?.molar_frac;
            if (compareVal === undefined || compareVal === null) return "-";
            
            // 判断是否为数值，只有数值才计算误差百分比
            const currentNum = parseFloat(mat.molar_frac);
            const compareNum = parseFloat(compareVal);
            const isNumeric = !isNaN(currentNum) && !isNaN(compareNum);
            
            if (isNumeric) {
              const errorPercent = (compareNum !== 0)
                ? calculateErrorPercent(compareNum, currentNum)
                : (currentNum === 0) ? "0.00%" : "100.00%";
              return `${compareVal}(${errorPercent})`;
            } else {
              // 非数值类型，直接显示
              return `${compareVal}`;
            }
          }),
        });
      });
    }

    return rows;
  };

  // ==============================================
  // 原始布局渲染函数（无对比时使用）
  // ==============================================
  const renderParamRows = (data: any, type: "elements" | "streams") => {
    if (!data) return null;
    const fieldConfig = fieldMap[type];
    let params: any = type === "streams" ? data.params : { ...data, ...data.params };
    
    return Object.entries(fieldConfig).map(([key, config]) => {
      if (params[key] === undefined || params[key] === null) return null;
      let value = config.enum?.[params[key]] ?? params[key];
      const unit = params[config.unitKey] ?? config.unit;
      return (
        <div key={key} className="paramRow">
          <span className="paramLabel">{config.name}</span>
          <span className="paramValue">
            {value} {unit && <span className="paramUnit">{unit}</span>}
          </span>
        </div>
      );
    });
  };

  const renderComponentRows = (materials: any[]) => {
    if (!materials || materials.length === 0) return null;
    return materials.map((mat: any) => (
      <div key={mat.cas} className="paramRow">
        <span className="paramLabel">
          {mat.cas === "7732-18-5" ? "水" : mat.cas === "67-56-1" ? "甲醇" : rebuildFormula(mat.name)}
        </span>
        <span className="paramValue">{mat.molar_frac}</span>
      </div>
    ));
  };

  const renderFeedPositions = (Ns: number[], inputStreams: any[]) => {
    if (!Ns || !inputStreams || Ns.length !== inputStreams.length) return null;
    return inputStreams.map((stream, idx) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name} 进料位置`}</span>
        <span className="paramValue">{Ns[idx]}</span>
      </div>
    ));
  };

  const renderProductPositions = (No: number[], outputStreams: any[]) => {
    if (!No || !outputStreams || No.length !== outputStreams.length) return null;
    return outputStreams.map((stream, idx) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name} 出料位置`}</span>
        <span className="paramValue">{No[idx]}</span>
      </div>
    ));
  };

  const elementRows = getElementTableRows();
  const streamRows = getStreamTableRows();
  const isCompareMode = compareItems.length > 0;

  // 复制
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(input, null, 2)).then(() => message.success("复制成功"));
  };

  // ==============================================
  // 下拉面板
  // ==============================================
  const dropdownContent = (
    <div style={{ width: 300, padding: 12, maxHeight: 400, overflow: "auto", background: "#f0efefff" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <Button size="small" type={filterType === "all" ? "primary" : "default"} onClick={() => setFilterType("all")}>全部</Button>
        <Button size="small" type={filterType === "design" ? "primary" : "default"} onClick={() => setFilterType("design")}>设计值</Button>
        <Button size="small" type={filterType === "actual" ? "primary" : "default"} onClick={() => setFilterType("actual")}>运行数据</Button>
        <Button size="small" type={filterType === "task" ? "primary" : "default"} onClick={() => setFilterType("task")}>其他任务</Button>
      </div>
      <div>
        {loading ? <div style={{ textAlign: "center", padding: 20 }}><Spin /></div> :
          filteredList.length === 0 ? <div style={{ textAlign: "center", color: "#999" }}>暂无数据</div> :
            filteredList.map(item => (
              <div key={item.id} style={{ padding: "8px 10px", border: "1px solid #eee", borderRadius: 4, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                <span>{item.source === 'task'? `${item.name || item.id}`: `${item.type === "design" ? "设计值" : "运行数据"} ${item.id}`
                                }</span>
                <Checkbox checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} />
              </div>
            ))}
      </div>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between" }}>
        <span>已选：{selectedIds.length} 项</span>
        <Button type="primary" size="small" onClick={handleConfirm} loading={confirmLoading}>
          {confirmLoading ? "正在对比..." : "确认选择"}
        </Button>
      </div>
    </div>
  );

  return (
    <ReactFlowProvider>
      {state.curFlow && state.curProject && (
        <>
          <PageTop
            mainContent={
              <div className="settingSelectorArea">
                <PageSeletor />
              </div>
            }
          />
          <div className="mainContent">
            <div className="mainTask" style={{ width: "90%" }}>
              {tabList.length > 0 && (
                <div className="tabContainer">
                  <div className="tabTitle">计算参数：</div>
                  <div className="tabButtonGroup">
                    {tabList.map(tab => (
                      <div key={tab.key} onClick={() => { setShowFullInput(false); setActiveTab(tab.key); }} className={`tabItem ${activeTab === tab.key ? "tabActive" : ""}`}>
                        <span className="tabText">{tab.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="contentLayout">
                <div className="sidebar">
                  <Button type="primary" style={{ marginBottom: 12, width: "100%" }} onClick={() => { setShowFullInput(true); setActiveElement(null); setActiveStream(null); }}>查看完整输入</Button>
                  <Dropdown overlay={dropdownContent} trigger={["click"]} open={dropdownOpen} onOpenChange={handleDropdownOpen} placement="bottomRight" overlayStyle={{ zIndex: 100002 }}>
                    <Button type="primary" style={{ marginBottom: 12, width: "100%" }}>对比设计值/运行数据 <DownOutlined /></Button>
                  </Dropdown>

                  {compareItems.length > 0 && (
                    <div style={{ marginBottom: 16, fontSize: 12, color: "#165DFF" }}>
                      已对比 {compareItems.length} 项
                    </div>
                  )}

                  <div className="sidebarSection">
                    <div className="sidebarTitle" onClick={() => setIsDeviceOpen(!isDeviceOpen)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
                      设备 {isDeviceOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                    </div>
                    <div className="sidebarList" style={{ display: isDeviceOpen ? "block" : "none" }}>
                      {elements.map(el => (
                        <div key={el.name} onClick={() => { setShowFullInput(false); setActiveElement(el.name); setActiveStream(null); }} className={`sidebarItem ${activeElement === el.name ? "sidebarItemActive" : ""}`}>{el.name}</div>
                      ))}
                    </div>
                  </div>

                  <div className="sidebarSection">
                    <div className="sidebarTitle" onClick={() => setIsStreamOpen(!isStreamOpen)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}>
                      流股 {isStreamOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
                    </div>
                    <div className="sidebarList" style={{ display: isStreamOpen ? "block" : "none" }}>
                      {streams.map(s => (
                        <div key={s.name} onClick={() => { setShowFullInput(false); setActiveStream(s.name); setActiveElement(null); }} className={`sidebarItem ${activeStream === s.name ? "sidebarItemActive" : ""}`}>{s.name}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="paramPanel">
                  {showFullInput ? (
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                        <h2 style={{ margin: 0 }}>完整输入数据</h2>
                        <Button icon={<CopyOutlined />} onClick={handleCopy}>一键复制</Button>
                      </div>
                      <ReactJson src={input} collapsed={false} displayDataTypes={false} enableClipboard={false} indentWidth={2} name={null} style={{ fontSize: 13 }} />
                    </div>
                  ) : isCompareMode ? (
                    // 有对比 → 表格展示
                    currentEl ? (
                      <div className="paramContent">
                        <h2 className="paramTitle">{currentEl.name} - 设备参数对比</h2>
                        <table className="compare-table">
                          <thead>
                            <tr>
                              {tableColumns.map(col => (
                                <th key={col.key}>{col.title}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {elementRows.map((row, i) => (
                              <tr key={i}>
                                <td>{row.label}</td>
                                <td className="compare-value-current">{row.current}</td>
                                {row.compares.map((v, j) => (
                                  <td key={j} className={compareItems[j]?.type === "design" ? "compare-value-design" : "compare-value-actual"}>{v}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : currentSt ? (
                      <div className="paramContent">
                        <h2 className="paramTitle">{currentSt.name} - 流股参数对比</h2>
                        <table className="compare-table">
                          <thead>
                            <tr>
                              {tableColumns.map(col => (
                                <th key={col.key}>{col.title}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {streamRows.map((row, i) => (
                              <tr key={i}>
                                <td>{row.label}</td>
                                <td className="compare-value-current">{row.current}</td>
                                {row.compares.map((v, j) => (
                                  <td key={j} className={compareItems[j]?.type === "design" ? "compare-value-design" : "compare-value-actual"}>{v}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null
                  ) : (
                    // 无对比 → 原始布局
                    currentEl ? (
                      <div className="paramContent">
                        <h2 className="paramTitle">{currentEl.name} 参数</h2>
                        <div className="paramGrid">
                          {renderParamRows(currentEl, "elements")}
                        </div>

                        {currentEl.params?.Ns && currentEl.inputStreams && (
                          <>
                            <h3 className="sectionTitle">进料流股位置</h3>
                            <div className="paramGrid">
                              {renderFeedPositions(currentEl.params.Ns, currentEl.inputStreams)}
                            </div>
                          </>
                        )}

                        {currentEl.params?.No && currentEl.outputStreams && (
                          <>
                            <h3 className="sectionTitle">出料流股位置</h3>
                            <div className="paramGrid">
                              {renderProductPositions(currentEl.params.No, currentEl.outputStreams)}
                            </div>
                          </>
                        )}

                        {currentEl.params?.eff && currentEl.params.eff.length > 0 && (
                          <>
                            <h3 className="sectionTitle">塔板效率</h3>
                            <div className="paramGrid">
                              {currentEl.params.eff.map((e, idx) => (
                                <React.Fragment key={idx}>
                                  <div className="paramRow">
                                    <span className="paramLabel">起始塔板 {idx+1}</span>
                                    <span className="paramValue">{e.start}</span>
                                  </div>
                                  <div className="paramRow">
                                    <span className="paramLabel">结束塔板 {idx+1}</span>
                                    <span className="paramValue">{e.end}</span>
                                  </div>
                                  <div className="paramRow">
                                    <span className="paramLabel">效率值 {idx+1}</span>
                                    <span className="paramValue">{e.value}</span>
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : currentSt ? (
                      <div className="paramContent">
                        <h2 className="paramTitle">{currentSt.name} 参数</h2>
                        <div className="paramGrid">
                          {renderParamRows(currentSt, "streams")}
                        </div>

                        {currentSt.materials?.length > 0 && (
                          <>
                            <h2 className="paramTitle" style={{marginTop:20}}>组分摩尔分率</h2>
                            <div className="paramGrid">
                              {renderComponentRows(currentSt.materials)}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="emptyTip">请选择设备或流股查看参数</div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </ReactFlowProvider>
  );
};

export default Component;